import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import DOMPurify from 'dompurify';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { X, ListTodo, Loader2, Loader, Sparkles, Bold, Italic, List, ListOrdered, Link as LinkIcon, Plus } from 'lucide-react';
import { useProjectStore } from '../../lib/stores/projectStore';
import { useUserStore } from '../../lib/stores/userStore';
import { useClientStore } from '../../lib/stores/clientStore';
import type { MailMessage } from '../../lib/stores/mail/types';
import { useAuth } from '../../lib/auth';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { useApp } from '../AppProvider';
import { supabase } from '../../lib/supabase';
import { useActivityTypesStore } from '../../lib/stores/activityTypesStore';

interface MailTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  messages: MailMessage[];
  threadId: string;
}

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Bassa', color: 'text-gray-500' },
  { value: 'medium', label: 'Media', color: 'text-blue-500' },
  { value: 'high', label: 'Alta', color: 'text-orange-500' },
  { value: 'urgent', label: 'Urgente', color: 'text-red-500' },
];

// Converte testo semplice (con a capo) in HTML per l'editor TipTap
function plainToHtml(text: string): string {
  if (!text) return '';
  if (/<[a-z][\s\S]*>/i.test(text)) return text; // già HTML
  return text.split(/\n{2,}/).map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
}

// Descrizione precompilata dall'email: usa il corpo HTML (sanificato) per mantenere
// la formattazione; fallback al testo semplice se l'HTML non è disponibile.
function emailToDescriptionHtml(m: MailMessage): string {
  const sender = (m.from_name || m.from_email || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const body = m.body_html ? DOMPurify.sanitize(m.body_html) : plainToHtml(m.body_text || m.snippet || '');
  return `<p><strong>Email da:</strong> ${sender}</p>${body}`;
}

const ACTIVITY_TYPES = [
  { value: 'task', label: 'Task' },
  { value: 'call', label: 'Chiamata' },
  { value: 'email', label: 'Email' },
  { value: 'meeting', label: 'Riunione' },
  { value: 'videochiamata', label: 'Videochiamata' },
  { value: 'appuntamento', label: 'Appuntamento' },
  { value: 'sopralluogo', label: 'Sopralluogo' },
  { value: 'note', label: 'Nota' },
  { value: 'other', label: 'Altro' },
];

export function MailTaskModal({ isOpen, onClose, messages }: MailTaskModalProps) {
  const { user } = useAuth();
  const { settings } = useSettingsStore();
  const { createTask, fetchProjects, createProject, createTaskSection } = useProjectStore();
  const searchClientsAndLeads = useClientStore(s => s.searchClientsAndLeads);
  const { toast } = useApp();

  // Tipi attività custom (per-org) — mostrati in coda alle opzioni di sistema
  // nella select "Tipo" della task commerciale (stesso pattern di DealActivityForm.tsx)
  const { customTypes, fetched: customTypesFetched, fetchCustomTypes } = useActivityTypesStore();
  useEffect(() => {
    if (!customTypesFetched) fetchCustomTypes();
  }, [customTypesFetched, fetchCustomTypes]);
  const activeCustomTypes = customTypes.filter(t => t.active);

  // Sezioni del progetto + creazione al volo
  const [sections, setSections] = useState<any[]>([]);
  const [sectionId, setSectionId] = useState('');
  const [sectionSearch, setSectionSearch] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [creatingSection, setCreatingSection] = useState(false);

  // Modalità: task di progetto oppure sales activity (su lead/cliente)
  const [mode, setMode] = useState<'task' | 'activity'>('task');
  const [activityType, setActivityType] = useState('task');
  const [target, setTarget] = useState<{ id: string; name: string } | null>(null);
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState<any[]>([]);
  const [searchingClients, setSearchingClients] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [projectId, setProjectId] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiPrompt, setShowAiPrompt] = useState(false);
  const [aiInstruction, setAiInstruction] = useState('');

  // Projects & users
  const projects = useProjectStore(s => s.projects);
  const users = useUserStore(s => s.users);
  const fetchUsers = useUserStore(s => s.fetchUsers);

  // Progetti separati: interni (Obly, senza cliente) vs commerciali (con cliente)
  const internalProjects = projects.filter(p => !(p as any).clientId && !(p as any).client_id);
  const commercialProjects = projects.filter(p => (p as any).clientId || (p as any).client_id);

  const organizationId = settings?.organization_id || (user as any)?.user_metadata?.organization_id;

  // Cliente collegato al thread (per pre-selezionare il target della task commerciale)
  const linkedClient = messages.find(m => m.client)?.client || null;

  // Editor TipTap per la descrizione (come nelle task)
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false, codeBlock: false, horizontalRule: false }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-[#2383E2] underline' } }),
      Placeholder.configure({ placeholder: 'Descrizione...' }),
    ],
    content: '',
    onUpdate: ({ editor }) => setDescription(editor.getHTML()),
  });

  // Sincronizza l'editor quando la descrizione cambia da fuori (AI/email)
  useEffect(() => {
    if (editor && !editor.isDestroyed && description !== editor.getHTML()) {
      editor.commands.setContent(description || '', false);
    }
  }, [description, editor]);

  // Analyze + carica progetti/utenti all'apertura
  useEffect(() => {
    if (!isOpen) return;
    fetchProjects();
    fetchUsers();
    setAssignedTo(user?.id || '');
    setMode('task');
    setClientSearch('');
    setClientResults([]);
    if (linkedClient) setTarget({ id: linkedClient.id, name: linkedClient.business_name || (linkedClient as any).contact_name || 'Cliente' });
    else setTarget(null);
    prefillFromEmail();
  }, [isOpen]);

  // Ricerca lead/clienti (modalità activity)
  useEffect(() => {
    if (!isOpen || mode !== 'activity') return;
    const q = clientSearch.trim();
    if (q.length < 2) { setClientResults([]); return; }
    setSearchingClients(true);
    const h = setTimeout(async () => {
      const res = await searchClientsAndLeads(q);
      setClientResults(res);
      setSearchingClients(false);
    }, 300);
    return () => clearTimeout(h);
  }, [clientSearch, mode, isOpen, searchClientsAndLeads]);

  // Precompila titolo + descrizione direttamente dall'email (niente analisi AI automatica;
  // per la riscrittura AI c'è il bottone "Scrivi con AI" on-demand).
  const prefillFromEmail = () => {
    const latest = messages[messages.length - 1];
    if (!latest) return;
    setTitle(latest.subject || '');
    setDescription(emailToDescriptionHtml(latest));
  };

  // Carica le sezioni (task header) del progetto selezionato
  const loadSections = async (pid: string) => {
    const { data } = await supabase
      .from('tasks')
      .select('id, title, section')
      .eq('project_id', pid)
      .eq('is_header', true)
      .order('order', { ascending: true });
    setSections(data || []);
  };
  useEffect(() => {
    setSectionId(''); setSectionSearch('');
    if (projectId) loadSections(projectId); else setSections([]);
  }, [projectId]);

  // Crea progetto al volo
  const handleCreateProject = async (name: string) => {
    if (!name.trim()) return;
    setCreatingProject(true);
    try {
      const created = await createProject({ name: name.trim(), status: 'active' } as any);
      if (created) {
        await fetchProjects();
        setProjectId(created.id);
        setProjectSearch('');
        toast.success('Progetto creato');
      }
    } catch { toast.error('Errore nella creazione del progetto'); }
    finally { setCreatingProject(false); }
  };

  // Crea sezione al volo nel progetto selezionato
  const handleCreateSection = async (name: string) => {
    if (!projectId || !name.trim()) return;
    setCreatingSection(true);
    try {
      const created = await createTaskSection(projectId, name.trim());
      await loadSections(projectId);
      if ((created as any)?.section) setSectionId((created as any).section);
      setSectionSearch('');
      toast.success('Sezione creata');
    } catch { toast.error('Errore nella creazione della sezione'); }
    finally { setCreatingSection(false); }
  };

  // Bottone AI: riscrive titolo + descrizione in modo chiaro e professionale
  const handleAiWrite = async (instruction?: string) => {
    setShowAiPrompt(false);
    setAiLoading(true);
    try {
      const descText = description.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      const system = 'Trasformi il contenuto di un\'email in una task CRM chiara, sintetica e professionale in italiano.'
        + (instruction && instruction.trim() ? ` Applica questa indicazione dell'utente: «${instruction.trim()}».` : '')
        + ' Rispondi SOLO con JSON valido (senza markdown fence): {"title":"titolo breve e azionabile","description":"HTML semplice con tag p, strong, em, ul, li"}.';
      const { data, error } = await supabase.functions.invoke('ai-chat', {
        body: {
          messages: [
            { role: 'system', content: system },
            { role: 'user', content: `Oggetto: ${title}\n\nContenuto:\n${descText}` },
          ],
          temperature: 0.4,
        },
      });
      if (error) throw error;
      let content = (data?.content || '').trim();
      const start = content.indexOf('{');
      const end = content.lastIndexOf('}');
      if (start >= 0 && end > start) content = content.slice(start, end + 1);
      const parsed = JSON.parse(content);
      if (parsed.title) setTitle(parsed.title);
      if (parsed.description) setDescription(parsed.description);
      setAiInstruction('');
      toast.success('Task riscritta con AI');
    } catch (e) {
      console.error('[MailTaskModal] AI write error:', e);
      toast.error('Errore nella riscrittura AI');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Inserisci il titolo');
      return;
    }

    console.log('[MailTaskModal] SAVE', { mode, projectId, sectionId, assignedTo });
    setSaving(true);
    try {
      if (mode === 'activity') {
        // Sales activity su lead/cliente
        if (!target) { toast.error('Seleziona un lead o cliente'); setSaving(false); return; }
        if (!dueDate) { toast.error('La task commerciale richiede una scadenza'); setSaving(false); return; }
        const { data: inserted, error } = await supabase.from('sales_activities').insert({
          title: title.trim(),
          description: description.trim() || null,
          type: activityType,
          status: 'pending',
          due_date: dueDate,
          client_id: target.id,
          user_id: user?.id,
          organization_id: organizationId,
          assigned_to: assignedTo || user?.id || null,
        }).select().single();
        if (error) throw error;
        // Evento workflow
        try {
          const { eventBus } = await import('../../lib/events/eventBus');
          eventBus.emitEvent('lead:activity_created' as any, {
            leadId: target.id,
            activityId: inserted?.id,
            activityType,
            title: title.trim(),
            assignedTo: assignedTo || user?.id,
          });
        } catch {}
        toast.success('Task commerciale creata su ' + target.name);
      } else {
        // Task di progetto (o generica)
        await createTask({
          project_id: projectId || undefined,
          section: projectId && sectionId ? sectionId : undefined,
          title: title.trim(),
          description: description.trim(),
          priority: priority as any,
          status: 'todo',
          assigned_to: assignedTo || undefined,
          due_date: dueDate || undefined,
          created_by: user?.id,
        });
        toast.success(projectId ? 'Task creata con successo' : 'Task generica creata');
      }
      onClose();
    } catch (error) {
      console.error('[MailTaskModal] Save error:', error);
      toast.error('Errore nella creazione');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[100] bg-black/30 md:top-12" onClick={onClose} />

      {/* Drawer laterale */}
      <div className="fixed top-0 right-0 bottom-0 z-[101] w-full max-w-xl bg-white dark:bg-gray-900 shadow-2xl border-l border-black/10 dark:border-gray-700 flex flex-col md:top-12 animate-[slideInRight_0.25s_ease-out]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-800">
          <div className="flex items-center gap-2">
            <ListTodo className="h-5 w-5 text-[#2383E2]" />
            <h3 className="text-base font-semibold text-[#37352F] dark:text-white">
              Crea task da email
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 text-[#787774]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Toggle modalità: Task progetto / Sales activity */}
          <div className="flex gap-1 p-1 bg-[rgba(55,53,47,0.04)] dark:bg-white/5 rounded-lg">
            <button
              onClick={() => setMode('task')}
              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${mode === 'task' ? 'bg-white dark:bg-gray-700 text-[#37352F] dark:text-white shadow-sm' : 'text-[#787774] dark:text-white/50'}`}
            >
              Task progetto
            </button>
            <button
              onClick={() => setMode('activity')}
              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${mode === 'activity' ? 'bg-white dark:bg-gray-700 text-[#37352F] dark:text-white shadow-sm' : 'text-[#787774] dark:text-white/50'}`}
            >
              Task commerciale
            </button>
          </div>

          {mode === 'task' ? (
            <>
            {/* Progetto (opzionale: vuoto = task generica) — ricerca + creazione al volo */}
            <div>
              <label className="text-xs font-medium text-[#787774] dark:text-white/40 mb-1 block">
                Progetto <span className="text-[#9B9A97]">(opzionale)</span>
              </label>
              {projectId ? (
                <div className="flex items-center justify-between px-3 py-2 bg-[#2383E2]/8 border border-[#2383E2]/20 rounded-lg">
                  <span className="text-sm text-[#37352F] dark:text-white truncate">{projects.find(p => p.id === projectId)?.name || 'Progetto'}</span>
                  <button onClick={() => { setProjectId(''); setProjectSearch(''); }} className="text-[#787774] hover:text-red-500" title="Rimuovi progetto (task generica)"><X className="h-3.5 w-3.5" /></button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    placeholder="Task generica — cerca un progetto per assegnarla..."
                    className="w-full px-3 py-2 text-sm bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2383E2] text-[#37352F] dark:text-white"
                  />
                  {projectSearch.trim().length >= 1 && (() => {
                    const q = projectSearch.trim().toLowerCase();
                    const term = projectSearch.trim();
                    // cerca per nome progetto E per nome cliente del progetto
                    const match = (p: any) => (p.name?.toLowerCase().includes(q)) || ((p.client?.business_name || '').toLowerCase().includes(q));
                    const fi = internalProjects.filter(match);
                    const fc = commercialProjects.filter(match);
                    const exact = projects.some((p: any) => p.name?.toLowerCase() === q);
                    return (
                      <div className="absolute top-full left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.12)] dark:border-gray-700 py-1 z-10">
                        {fi.length > 0 && <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#9B9A97]">Progetti Obly</div>}
                        {fi.map((p: any) => (
                          <button key={p.id} onClick={() => { setProjectId(p.id); setProjectSearch(''); }} className="w-full text-left px-3 py-1.5 text-sm text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 truncate">{p.name}</button>
                        ))}
                        {fc.length > 0 && <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#9B9A97]">Progetti commerciali</div>}
                        {fc.map((p: any) => (
                          <button key={p.id} onClick={() => { setProjectId(p.id); setProjectSearch(''); }} className="w-full text-left px-3 py-1.5 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5">
                            <div className="text-sm text-[#37352F] dark:text-white truncate">{p.name}</div>
                            {p.client?.business_name && <div className="text-[11px] text-[#787774] dark:text-white/40 truncate">{p.client.business_name}</div>}
                          </button>
                        ))}
                        {fi.length === 0 && fc.length === 0 && <div className="px-3 py-1.5 text-xs text-[#787774]">Nessun progetto trovato</div>}
                        {!exact && (
                          <>
                            <div className="border-t border-[rgba(55,53,47,0.08)] dark:border-gray-700 my-1" />
                            <button onClick={() => handleCreateProject(term)} disabled={creatingProject} className="w-full text-left px-3 py-1.5 text-sm text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 flex items-center gap-1.5 disabled:opacity-50">
                              {creatingProject ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Crea progetto «{term}»
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Sezione del progetto (cercabile + creazione al volo) */}
            {projectId && (
              <div>
                <label className="text-xs font-medium text-[#787774] dark:text-white/40 mb-1 block">
                  Sezione <span className="text-[#9B9A97]">(opzionale)</span>
                </label>
                {sectionId ? (
                  <div className="flex items-center justify-between px-3 py-2 bg-[#2383E2]/8 border border-[#2383E2]/20 rounded-lg">
                    <span className="text-sm text-[#37352F] dark:text-white truncate">{sections.find(s => s.section === sectionId)?.title || 'Sezione'}</span>
                    <button onClick={() => { setSectionId(''); setSectionSearch(''); }} className="text-[#787774] hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      value={sectionSearch}
                      onChange={(e) => setSectionSearch(e.target.value)}
                      placeholder="Nessuna sezione — cerca o crea..."
                      className="w-full px-3 py-2 text-sm bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2383E2] text-[#37352F] dark:text-white"
                    />
                    {(sectionSearch.trim().length >= 1 || sections.length > 0) && (() => {
                      const q = sectionSearch.trim().toLowerCase();
                      const term = sectionSearch.trim();
                      const fs = q ? sections.filter(s => s.title?.toLowerCase().includes(q)) : sections;
                      const exact = sections.some(s => s.title?.toLowerCase() === q);
                      return (
                        <div className="absolute top-full left-0 right-0 mt-1 max-h-44 overflow-y-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.12)] dark:border-gray-700 py-1 z-10">
                          {fs.map(s => (
                            <button key={s.id} onClick={() => { setSectionId(s.section); setSectionSearch(''); }} className="w-full text-left px-3 py-1.5 text-sm text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 truncate">{s.title}</button>
                          ))}
                          {fs.length === 0 && <div className="px-3 py-1.5 text-xs text-[#787774]">Nessuna sezione</div>}
                          {term && !exact && (
                            <>
                              <div className="border-t border-[rgba(55,53,47,0.08)] dark:border-gray-700 my-1" />
                              <button onClick={() => handleCreateSection(term)} disabled={creatingSection} className="w-full text-left px-3 py-1.5 text-sm text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 flex items-center gap-1.5 disabled:opacity-50">
                                {creatingSection ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Crea sezione «{term}»
                              </button>
                            </>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
            </>
          ) : (
            /* Sales activity: target lead/cliente + tipo */
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-[#787774] dark:text-white/40 mb-1 block">Lead / Cliente *</label>
                {target ? (
                  <div className="flex items-center justify-between px-3 py-2 bg-[#2383E2]/8 border border-[#2383E2]/20 rounded-lg">
                    <span className="text-sm text-[#37352F] dark:text-white truncate">{target.name}</span>
                    <button onClick={() => { setTarget(null); setClientSearch(''); }} className="text-[#787774] hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      placeholder="Cerca lead o cliente..."
                      className="w-full px-3 py-2 text-sm bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2383E2] text-[#37352F] dark:text-white"
                    />
                    {(searchingClients || clientResults.length > 0) && (
                      <div className="absolute top-full left-0 right-0 mt-1 max-h-44 overflow-y-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.12)] dark:border-gray-700 py-1 z-10">
                        {searchingClients && <div className="px-3 py-2 text-xs text-[#787774] flex items-center gap-1.5"><Loader className="h-3 w-3 animate-spin" /> Ricerca…</div>}
                        {clientResults.map((c: any) => (
                          <button key={c.id} onClick={() => { setTarget({ id: c.id, name: c.business_name || c.trade_name || 'Cliente' }); setClientResults([]); }} className="w-full text-left px-3 py-1.5 text-sm text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 truncate">
                            {c.business_name || c.trade_name || 'Senza nome'}{c.email ? ` · ${c.email}` : ''}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-[#787774] dark:text-white/40 mb-1 block">Tipo</label>
                <select
                  value={activityType}
                  onChange={(e) => setActivityType(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2383E2] text-[#37352F] dark:text-white"
                >
                  {ACTIVITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  {activeCustomTypes.length > 0 && (
                    <>
                      <option disabled>──── Tipi personalizzati ────</option>
                      {activeCustomTypes.map(ct => (
                        <option key={ct.slug} value={ct.slug}>{ct.name}</option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            </div>
          )}

          {/* Assegnatario + scadenza */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-[#787774] dark:text-white/40 mb-1 block">Assegna a</label>
              {assignedTo ? (
                <div className="flex items-center justify-between px-3 py-2 bg-[#2383E2]/8 border border-[#2383E2]/20 rounded-lg">
                  <span className="text-sm text-[#37352F] dark:text-white truncate">
                    {(() => { const u = users.find(x => x.id === assignedTo); return u ? `${u.full_name || u.email}${u.id === user?.id ? ' (io)' : ''}` : 'Utente'; })()}
                  </span>
                  <button onClick={() => { setAssignedTo(''); setAssigneeSearch(''); }} className="text-[#787774] hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    value={assigneeSearch}
                    onChange={(e) => setAssigneeSearch(e.target.value)}
                    placeholder="Cerca persona..."
                    className="w-full px-3 py-2 text-sm bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2383E2] text-[#37352F] dark:text-white"
                  />
                  {assigneeSearch.trim().length >= 1 && (() => {
                    const q = assigneeSearch.trim().toLowerCase();
                    const fu = users.filter(u => (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q));
                    return (
                      <div className="absolute top-full left-0 right-0 mt-1 max-h-44 overflow-y-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.12)] dark:border-gray-700 py-1 z-10">
                        {fu.length === 0 && <div className="px-3 py-2 text-xs text-[#787774]">Nessuno trovato</div>}
                        {fu.map(u => (
                          <button key={u.id} onClick={() => { setAssignedTo(u.id); setAssigneeSearch(''); }} className="w-full text-left px-3 py-1.5 text-sm text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 truncate">
                            {u.full_name || u.email}{u.id === user?.id ? ' (io)' : ''}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-[#787774] dark:text-white/40 mb-1 block">Scadenza{mode === 'activity' ? ' *' : ''}</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2383E2] text-[#37352F] dark:text-white"
              />
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="text-xs font-medium text-[#787774] dark:text-white/40 mb-1 block">
              Titolo *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2383E2] text-[#37352F] dark:text-white"
              placeholder="Titolo della task"
            />
          </div>

          {/* Description (editor TipTap) */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-[#787774] dark:text-white/40">Descrizione</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowAiPrompt(v => !v)}
                  disabled={aiLoading || (!title.trim() && !description.replace(/<[^>]+>/g, '').trim())}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 hover:bg-violet-100 dark:hover:bg-violet-900/30 transition-colors disabled:opacity-50"
                  title="Scrivi/migliora la task con l'AI"
                >
                  {aiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                  Scrivi con AI
                </button>
                {showAiPrompt && (
                  <div className="absolute top-full right-0 mt-1 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-[rgba(55,53,47,0.12)] dark:border-gray-700 p-3 z-30 space-y-2">
                    <p className="text-[11px] text-[#787774] dark:text-white/40">Cosa deve fare l'AI? (lascia vuoto per riscriverla bene)</p>
                    <textarea
                      value={aiInstruction}
                      onChange={(e) => setAiInstruction(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAiWrite(aiInstruction); }}
                      rows={2}
                      autoFocus
                      placeholder="Es. accorcia, tono più formale, aggiungi i prossimi passi..."
                      className="w-full px-2.5 py-1.5 text-xs bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-md focus:outline-none focus:ring-1 focus:ring-violet-400 text-[#37352F] dark:text-white resize-none"
                    />
                    <div className="flex flex-wrap gap-1">
                      {['Accorcia', 'Tono più formale', 'Aggiungi i prossimi passi', 'Estrai la richiesta principale', 'Traduci in inglese'].map(preset => (
                        <button key={preset} onClick={() => handleAiWrite(preset)} className="px-1.5 py-0.5 text-[10px] rounded bg-[rgba(55,53,47,0.06)] dark:bg-white/10 text-[#37352F] dark:text-white/80 hover:bg-violet-100 dark:hover:bg-violet-900/30">
                          {preset}
                        </button>
                      ))}
                    </div>
                    <div className="flex justify-end gap-1.5 pt-0.5">
                      <button onClick={() => setShowAiPrompt(false)} className="px-2 py-1 text-[11px] text-[#787774] hover:text-[#37352F] dark:hover:text-white">Annulla</button>
                      <button onClick={() => handleAiWrite(aiInstruction)} disabled={aiLoading} className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-md bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50">
                        {aiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Genera
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-lg overflow-hidden bg-[rgba(55,53,47,0.04)] dark:bg-white/5 focus-within:ring-1 focus-within:ring-[#2383E2]">
              {/* Toolbar */}
              <div className="flex items-center gap-0.5 px-2 py-1 border-b border-[rgba(55,53,47,0.06)] dark:border-gray-700">
                {[
                  { icon: Bold, action: () => editor?.chain().focus().toggleBold().run(), active: editor?.isActive('bold') },
                  { icon: Italic, action: () => editor?.chain().focus().toggleItalic().run(), active: editor?.isActive('italic') },
                  { icon: List, action: () => editor?.chain().focus().toggleBulletList().run(), active: editor?.isActive('bulletList') },
                  { icon: ListOrdered, action: () => editor?.chain().focus().toggleOrderedList().run(), active: editor?.isActive('orderedList') },
                  { icon: LinkIcon, action: () => { const url = window.prompt('URL:'); if (url) editor?.chain().focus().setLink({ href: url }).run(); }, active: editor?.isActive('link') },
                ].map(({ icon: Icon, action, active }, i) => (
                  <button key={i} type="button" onClick={action} className={`p-1.5 rounded transition-colors ${active ? 'bg-[rgba(55,53,47,0.1)] dark:bg-gray-600 text-[#37352F] dark:text-white' : 'text-[#787774] hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10'}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </button>
                ))}
              </div>
              <EditorContent editor={editor} className="gmail-task-desc px-3 py-2 text-sm text-[#37352F] dark:text-white" />
            </div>
            <style>{`
              .gmail-task-desc .ProseMirror { outline: none; min-height: 160px; line-height: 1.6; }
              .gmail-task-desc .ProseMirror p { margin: 0 0 6px 0; }
              .gmail-task-desc .ProseMirror ul, .gmail-task-desc .ProseMirror ol { margin: 6px 0; padding-left: 20px; }
              .gmail-task-desc .ProseMirror p.is-editor-empty:first-child::before { color: #9B9A97; content: attr(data-placeholder); float: left; height: 0; pointer-events: none; }
              .gmail-task-desc .ProseMirror a { color: #2383E2; text-decoration: underline; }
            `}</style>
          </div>

          {/* Priority (solo task di progetto) */}
          {mode === 'task' && (
          <div>
            <label className="text-xs font-medium text-[#787774] dark:text-white/40 mb-1 block">
              Priorità
            </label>
            <div className="flex gap-2">
              {PRIORITY_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setPriority(opt.value)}
                  className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                    priority === opt.value
                      ? 'bg-[#2383E2] text-white'
                      : 'bg-[rgba(55,53,47,0.04)] dark:bg-white/5 text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.08)]'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-[#787774] hover:text-[#37352F] dark:hover:text-white transition-colors"
          >
            Annulla
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !title.trim()}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#2383E2] text-white text-sm font-medium rounded-lg hover:bg-[#1a6fc9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ListTodo className="h-3.5 w-3.5" />
            )}
            {mode === 'activity' ? 'Crea task commerciale' : 'Crea task'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}
